import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Shield, Eye, EyeOff, CheckCircle, AlertCircle, ExternalLink, Loader2 } from 'lucide-react'
import AppLogo from '../components/AppLogo'
import { GeometricBackground } from '../components/layouts/GeometricBackground'
import { motion } from 'framer-motion'
import api from '../services/api'
import Loader from '../components/Loader'

function ForgotPassword() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1) // 1: Email, 2: OTP, 3: Reset Password
  const [email, setEmail] = useState('')
  const [previewUrl, setPreviewUrl] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [otpVerified, setOtpVerified] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const otpRefs = useRef([])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Email validation
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  // Password strength validation
  const validatePassword = (password) => {
    const minLength = password.length >= 8
    const hasNumber = /\d/.test(password)
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    return { minLength, hasNumber, hasSpecial, isValid: minLength && hasNumber && hasSpecial }
  }

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    setError('')
    setPreviewUrl('')

    try {
      const response = await api.post('/auth/send-otp', { email })

      if (response.data.success) {
        setStep(2)
        setResendCooldown(30)
        if (response.data.previewUrl) {
          setPreviewUrl(response.data.previewUrl)
        }
      } else {
        setError(response.data.error || 'Failed to send OTP')
      }
    } catch (err) {
      console.error('Send OTP error:', err)
      if (err.response?.status === 404) {
        setError('Email address not registered. Please register first.')
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.')
      } else {
        setError('Failed to send OTP. Please check your connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault()

    const otpString = otp.join('')
    if (otpString.length !== 6) {
      setError('Please enter complete 6-digit OTP')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await api.post('/auth/verify-otp', {
        email,
        otp: otpString
      })

      if (response.data.success) {
        setOtpVerified(true)
        setStep(3)
        setOtp(['', '', '', '', '', '']) // Clear OTP from state
      } else {
        setError(response.data.error || 'Invalid OTP')
      }
    } catch (err) {
      console.error('Verify OTP error:', err)
      if (err.response?.status === 400) {
        setError('Invalid or expired OTP')
      } else {
        setError('Failed to verify OTP. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault()

    if (!otpVerified) {
      setError('OTP verification required')
      return
    }

    const passwordValidation = validatePassword(newPassword)
    if (!passwordValidation.isValid) {
      setError('Password must be at least 8 characters with 1 number and 1 special character')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await api.post('/auth/reset-password', {
        email,
        newPassword
      })

      if (response.data.success) {
        // Clear sensitive state
        setEmail('')
        setNewPassword('')
        setConfirmPassword('')
        setOtpVerified(false)

        // Redirect to login with success message
        navigate('/login', {
          state: { message: 'Password reset successful. Please login with your new password.' }
        })
      } else {
        setError(response.data.error || 'Failed to reset password')
      }
    } catch (err) {
      console.error('Reset password error:', err)
      setError('Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return

    setLoading(true)
    setError('')
    setOtp(['', '', '', '', '', ''])

    try {
      const response = await api.post('/auth/send-otp', { email })

      if (response.data.success) {
        setResendCooldown(30)
      } else {
        setError(response.data.error || 'Failed to resend OTP')
      }
    } catch (err) {
      console.error('Resend OTP error:', err)
      setError('Failed to resend OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // OTP input handling
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)
    setError('')

    // Auto focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  // Step 1: Phone Number Input
  if (step === 1) {
    return (
      <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#030303] py-12 px-4 sm:px-6 lg:px-8">
        <GeometricBackground variant="forgot-password" />

        <div className="relative z-10 w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 w-full"
          >
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <AppLogo size="large" showImage={true} />
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">
                Forgot Password
              </h2>
              <p className="mt-2 text-sm text-white/60">
                Enter your registered email address to receive OTP
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSendOTP}>
              {error && (
                <div className="flex items-center space-x-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-white/40" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-12 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setError('')
                    }}
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-3 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="flex items-center justify-center space-x-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Login</span>
                </Link>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    )
  }

  // Step 2: OTP Verification
  if (step === 2) {
    return (
      <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#030303] py-12 px-4 sm:px-6 lg:px-8">
        <GeometricBackground variant="forgot-password" />

        <div className="relative z-10 w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 w-full"
          >
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div className="bg-indigo-500/20 p-4 rounded-full border border-indigo-500/30">
                  <Shield className="h-8 w-8 text-indigo-400" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">
                Verify OTP
              </h2>
              <p className="mt-2 text-sm text-white/60">
                Enter the 6-digit code sent to<br />
                <span className="text-white font-medium">{email}</span>
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleVerifyOTP}>
              {error && (
                <div className="flex items-center space-x-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              {previewUrl && (
                <div className="flex items-center space-x-2 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                  <ExternalLink className="h-5 w-5 text-blue-400" />
                  <div className="text-sm text-blue-300">
                    <p>Development Mode: </p>
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-blue-200">
                      Click here to view OTP Email
                    </a>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/80 mb-4 text-center">
                  Enter OTP
                </label>
                <div className="flex justify-center space-x-3">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      className="w-12 h-12 text-center text-lg font-bold border border-white/10 rounded-xl bg-white/5 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      disabled={loading}
                    />
                  ))}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-3 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-white/60 mb-2">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || loading}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep(1)
                    setOtp(['', '', '', '', '', ''])
                    setError('')
                  }}
                  className="flex items-center justify-center space-x-2 text-sm text-white/60 hover:text-white transition-colors mx-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Change Email Address</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    )
  }

  // Step 3: Reset Password
  const passwordValidation = validatePassword(newPassword)

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#030303] py-12 px-4 sm:px-6 lg:px-8">
      <GeometricBackground variant="forgot-password" />

      <div className="relative z-10 w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-8 w-full"
        >
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="bg-emerald-500/20 p-4 rounded-full border border-emerald-500/30">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Reset Password
            </h2>
            <p className="mt-2 text-sm text-white/60">
              Create a new secure password for your account
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleResetPassword}>
            {error && (
              <div className="flex items-center space-x-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-white/80 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all pr-12"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    setError('')
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/40 hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Password strength indicator */}
              {newPassword && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center space-x-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.minLength ? 'bg-emerald-500' : 'bg-white/20'}`}></div>
                    <span className={passwordValidation.minLength ? 'text-emerald-400' : 'text-white/40'}>
                      At least 8 characters
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.hasNumber ? 'bg-emerald-500' : 'bg-white/20'}`}></div>
                    <span className={passwordValidation.hasNumber ? 'text-emerald-400' : 'text-white/40'}>
                      At least 1 number
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.hasSpecial ? 'bg-emerald-500' : 'bg-white/20'}`}></div>
                    <span className={passwordValidation.hasSpecial ? 'text-emerald-400' : 'text-white/40'}>
                      At least 1 special character
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all pr-12"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setError('')
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/40 hover:text-white"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !passwordValidation.isValid || newPassword !== confirmPassword}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-3 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default ForgotPassword